import { uniqBy } from "es-toolkit/compat";

import type { ResolveActiveNodes } from "../../nodes/index.ts";
import type { GetDesignerNodeById, GetParentContextById } from "../../state/index.ts";
import type { ConditionBuildable, DesignerNode, GetManifestByTypeId } from "../../types/index.ts";

type Options = {
  includeChildren?: boolean;
};

export const getNodeConditionBuildablesCreate =
  (getManifest: GetManifestByTypeId, getParentContextById: GetParentContextById) =>
  (node: DesignerNode, { includeChildren }: Options = {}): ConditionBuildable[] => {
    const { designer } = getManifest(node.typeId);
    const buildables =
      designer && designer.getConditionBuildables
        ? designer
            .getConditionBuildables(node, { parentContext: getParentContextById(node.id) })
            .filter((c): c is Exclude<typeof c, undefined | false> => !!c)
            .map((buildable) => ({
              nodeId: node.id,
              ...buildable,
            }))
        : [];
    // arguable prod only as it's kind of fundamental
    if (uniqBy(buildables, "name").length !== buildables.length) {
      // could specify which but so obvious to fix
      throw new Error(
        `Buildable names not unique on 'getConditionBuildables()' on Node Designer of type ${node.typeId}`,
      );
    }

    if (!includeChildren) {
      return buildables;
    }

    const childBuildables = buildables.flatMap((b): ConditionBuildable[] =>
      b.children
        ? Object.values(b.children)
            .filter((c): c is NonNullable<typeof c> => !!c)
            .map((c) => ({
              ...c,
              nodeId: node.id,
            }))
        : [],
    );
    return [...buildables, ...childBuildables];
  };

export const resolveConditionBuildablesCreate =
  (
    resolveActiveNodes: ResolveActiveNodes,
    getNodeConditionBuildables: GetNodeConditionBuildables,
    _getById: GetDesignerNodeById,
    utilityBuildables?: ReadonlyArray<ConditionBuildable>,
  ) =>
  (nodeId: number, options: Options = {}): ConditionBuildable[] => {
    const buildables = resolveActiveNodes(nodeId).flatMap((node) => getNodeConditionBuildables(node, options));
    if (!utilityBuildables) {
      return buildables;
    }
    return [...buildables, ...utilityBuildables.map((b) => ({ ...b, nodeId }))];
  };

/**
 * The buildables a condition may be *added* from, as against the ones already
 * built conditions resolve through. Only the sidebar wants this list: everything
 * else — resolving a stored condition for editing, building it for publish —
 * needs the full one, or a retired buildable's conditions would vanish.
 */
export const resolveAddableConditionBuildablesCreate =
  (resolveConditionBuildables: ResolveConditionBuildables) =>
  (nodeId: number, options: Options = {}): ConditionBuildable[] =>
    resolveConditionBuildables(nodeId, options).filter((buildable) => !buildable.retired);

export type GetNodeConditionBuildables = ReturnType<typeof getNodeConditionBuildablesCreate>;

export type ResolveConditionBuildables = ReturnType<typeof resolveConditionBuildablesCreate>;

export type ResolveAddableConditionBuildables = ReturnType<typeof resolveAddableConditionBuildablesCreate>;
